import {
  AlertTriangle,
  Building2,
  Info,
  KeyRound,
  Lock,
  LockOpen,
  Mail,
  Network,
  PauseCircle,
  Power,
  RotateCcw
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import {
  apiClient,
  type AccessCompany,
  type AccessSuspension,
  type AccessUser,
  type AccessUserDetail,
  type CompanyGroup,
  type LoginMethod
} from "@/shared/api/api-client";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import { ROLE_LABEL } from "@/shared/utils/roles";
import { welcomeEmailNotice } from "@/shared/vocabulary/welcome-email";
import { parseServerTime } from "@/features/uploads/upload-metrics";
import { formatDateTime, formatFullDate } from "@/shared/utils/format";
import { AccessLinkList, inactiveGroupReason } from "./AccessLinkList";
import { Select } from "@/shared/components/Select";

export const STATUS_LABEL: Record<AccessUser["status"], string> = {
  active: "Ativo",
  pending: "Convite pendente",
  suspended: "Suspenso",
  disabled: "Inativo"
};

export const STATUS_TONE: Record<AccessUser["status"], "success" | "warning" | "inactive"> = {
  active: "success",
  pending: "warning",
  suspended: "warning",
  disabled: "inactive"
};

const SUSPENSION_REASON: Record<AccessSuspension["reason"], string> = {
  leave: "afastamento",
  inactivity: "inatividade (90 dias sem entrar)",
  recertification: "recertificação pendente"
};

export const LOGIN_METHOD_LABEL: Record<LoginMethod, string> = {
  google: "Conta Google",
  password: "Usuário e senha"
};

/** Conta "usuário e senha" suspensa por recertificação: é a exceção que
 *  venceu (2026-10-13), e reativar a renova por mais 90 dias. */
function expiredException(user: AccessUser): boolean {
  return user.login_method === "password" && user.suspension?.reason === "recertification";
}

function serverDate(value: string | null): Date | null {
  const time = parseServerTime(value);
  return time == null ? null : new Date(time);
}

function initials(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0])
      .join("")
      .toLocaleUpperCase("pt-BR") || "?"
  );
}

/** Espelha `change_role` do servidor: a troca que ENVOLVE Administrador ou
 *  Máster recomeça o escopo do zero (a tela manda listas vazias); entre
 *  Especialista, Técnico e Visualizador os vínculos ficam. */
function roleChangeResetsScope(from: AccessUser["role"], to: AccessUser["role"]): boolean {
  if (from === to) return false;
  const pivots: AccessUser["role"][] = ["administrator", "master"];
  return pivots.includes(from) || pivots.includes(to);
}

/** A observação é texto livre e vem antes de outra frase: sem pontuação
 *  final, as duas grudavam ("licença até 30/11 Cargo e vínculos…"). */
function withPeriod(text: string): string {
  const clean = text.trim();
  return /[.!?…]$/.test(clean) ? clean : `${clean}.`;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

type Confirmation = "disable" | "login-method" | "reset-password";

/** O aviso de identidade compartilhada (2026-10-20, decisão da dona:
 *  permitir, mas avisar). Senha e credencial do Keycloak valem pro realm
 *  da FSB inteiro, então a ação atinge os outros sistemas também. Vazio
 *  quando a conta é só do ReputAItion. */
export function sharedIdentityNote(user: AccessUserDetail, effect: string): string {
  const shared = user.shared_identity;
  if (!shared) return "";
  const where = shared.other_clients.length
    ? `também é usada em outros sistemas da FSB (${shared.other_clients.join(", ")})`
    : "já existia no Keycloak da FSB antes do convite e pode ser usada em outros sistemas";
  return ` Atenção: esta conta ${where}. ${effect}`;
}

/** Opções do `act` do pai: `reload: false` pra ação que não muda o que a
 *  tela mostra. */
export type ActOptions = { reload?: boolean };

/** Uma linha de "Gerenciar conta": o que a ação faz, à esquerda; o botão, à
 *  direita — ou embaixo, quando os botões não cabem ao lado de um texto
 *  legível. `tag` é o estado atual daquela configuração, ao lado do título. */
function ManageRow({
  title,
  tag,
  description,
  children
}: {
  title: string;
  tag?: string;
  description: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="access-manage-row">
      <div className="access-manage-text">
        <div className="access-manage-title">
          <strong>{title}</strong>
          {tag && <span className="access-manage-tag">{tag}</span>}
        </div>
        <p>{description}</p>
      </div>
      <div className="access-manage-action">{children}</div>
    </div>
  );
}

/**
 * O detalhe de uma pessoa em Usuários, em quatro blocos com ordem de
 * importância (redesenho de 2026-10-15 — a versão anterior punha as seis
 * ações, rotineiras e destrutivas, lado a lado e com o mesmo peso):
 *
 * 1. **Quem é e em que pé está**: iniciais, nome, e-mail, cargo e
 *    situação; e, quando a situação pede alguma coisa (convite pendente,
 *    suspensão, conta inativa), um aviso com A ação daquele momento.
 * 2. **Cadastro**: nome e cargo. O botão de salvar só aparece com
 *    mudança; trocar de cargo diz antes o que acontece com os vínculos.
 * 3. **Acessos**: núcleos e empresas — ou por que ainda não dá.
 * 4. **Gerenciar conta**: suspender, forma de entrar e inativar, cada um
 *    com a explicação do efeito. Inativar e trocar a forma de entrar
 *    pedem confirmação.
 *
 * Remontado por usuário (`key`), então rascunhos não vazam de uma pessoa
 * pra outra.
 */
export function UserDetailPanel({
  token,
  user,
  master,
  loginMode,
  busy,
  groups,
  companies,
  allowedRoles,
  statusLabel,
  act
}: {
  token: string;
  user: AccessUserDetail;
  /** Quem olha é o Máster. Senão é o Administrador, e a pessoa aberta é
   *  do núcleo dele e de cargo abaixo do dele (a lista só traz essas). */
  master: boolean;
  loginMode: string | null;
  busy: boolean;
  groups: CompanyGroup[];
  companies: AccessCompany[];
  allowedRoles: AccessUser["role"][];
  statusLabel: (user: AccessUser) => string;
  act: <T>(operation: () => Promise<T>, done?: string | ((result: T) => string), options?: ActOptions) => Promise<boolean>;
}) {
  // O nome do cadastro é o que vale no ReputAItion (2026-10-15), e quem o
  // corrige é quem administra a pessoa — ela não troca o próprio.
  const [nextName, setNextName] = useState(user.name);
  const [nextRole, setNextRole] = useState<AccessUser["role"]>(user.role);
  // Os núcleos de quem vira Administrador (2026-10-23, bug relatado): a
  // criação já exigia, a troca de cargo não — e ele ficava sem núcleo.
  const [adminGroups, setAdminGroups] = useState<CompanyGroup[]>([]);
  const [suspending, setSuspending] = useState(false);
  const [suspendNote, setSuspendNote] = useState("");
  const [confirming, setConfirming] = useState<Confirmation | null>(null);

  // O servidor pode devolver o nome normalizado (espaços): o campo segue
  // o que ficou gravado, senão "Alterar nome" reapareceria sozinho.
  useEffect(() => setNextName(user.name), [user.name]);
  useEffect(() => setNextRole(user.role), [user.role]);
  useEffect(() => setAdminGroups([]), [nextRole, user.role]);

  const nameChanged = nextName.trim() !== "" && nextName.trim() !== user.name;
  const roleChanged = nextRole !== user.role;
  const linkCount = user.groups.length + user.companies.length;
  const resetsScope = roleChangeResetsScope(user.role, nextRole) && linkCount > 0;
  // Virar Administrador recomeça o escopo do zero, e ele precisa de pelo
  // menos um núcleo (o servidor confere de novo em `change_role`).
  const needsNucleus = roleChanged && nextRole === "administrator";
  const google = user.login_method === "google";
  const otherMethod: LoginMethod = google ? "password" : "google";
  const canManage = user.status === "active" || user.status === "pending";

  const confirm = async (operation: () => Promise<unknown>, done?: string, options?: ActOptions) => {
    const ok = await act(operation, done, options);
    if (ok) setConfirming(null);
  };

  return (
    <>
      {/* 1. Quem é e em que pé está */}
      <div className="user-detail-head">
        <span className={`user-detail-avatar is-${STATUS_TONE[user.status]}`} aria-hidden="true">
          {initials(user.name)}
        </span>
        <div className="user-detail-identity">
          <h3>{user.name}</h3>
          <p className="user-detail-email" title={user.email}>{user.email}</p>
          <div className="user-detail-chips">
            <span className="user-detail-role">{ROLE_LABEL[user.role]}</span>
            <span className={`badge-pill ${STATUS_TONE[user.status]}`}>{statusLabel(user)}</span>
          </div>
        </div>
      </div>

      {/* Pendente: o aviso logo abaixo já diz que a pessoa não entrou,
          e "Último acesso: nunca" repetia a mesma coisa. */}
      {(user.status !== "pending" || loginMode || (user.login_method === "password" && user.login_exception_until)) && (
      <dl className="access-facts">
        {user.status !== "pending" && (
          <div>
            <dt>Último acesso</dt>
            <dd>{user.last_login_at ? formatFullDate(serverDate(user.last_login_at)) : "Nunca entrou"}</dd>
          </div>
        )}
        {loginMode && (
          <div>
            <dt>Entra com</dt>
            <dd>{LOGIN_METHOD_LABEL[user.login_method]}</dd>
          </div>
        )}
        {user.login_method === "password" && user.login_exception_until && (
          <div>
            <dt>Exceção válida até</dt>
            <dd>{formatFullDate(serverDate(user.login_exception_until))}</dd>
          </div>
        )}
      </dl>
      )}

      {user.status === "pending" && (
        <div className="user-detail-callout">
          <Mail size={16} aria-hidden="true" />
          <p>
            {loginMode && google
              ? <>Ainda não entrou. O acesso é pela conta Google de <strong>{user.email}</strong>.</>
              : <>Convite enviado. Falta a pessoa definir a senha e entrar pela primeira vez.</>}
          </p>
          <button
            className="secondary-button"
            disabled={busy}
            onClick={() => void act(
              () => apiClient.resendAccessInvite(token, user.id),
              ({ welcome_email: welcome }) => welcome
                ? `Convite reenviado para ${user.email}. ${welcomeEmailNotice(welcome, user.email)}`
                : `Convite reenviado para ${user.email}.`
            )}
          >
            Reenviar convite
          </button>
        </div>
      )}

      {user.suspension && (
        <div className="user-detail-callout is-warning" role="status">
          <AlertTriangle size={16} aria-hidden="true" />
          <p>
            Suspenso por{" "}
            {expiredException(user) ? "exceção de login por senha vencida" : SUSPENSION_REASON[user.suspension.reason]}{" "}
            em {formatDateTime(serverDate(user.suspension.suspended_on))}
            {user.suspension.suspended_by_name
              ? ` por ${user.suspension.suspended_by_name}`
              : user.suspension.reason === "inactivity"
                ? " pela rotina automática"
                : ""}
            .{user.suspension.note && <> Observação: {withPeriod(user.suspension.note)}</>}{" "}
            Cargo e vínculos ficam guardados e voltam com a reativação.
          </p>
          {user.status === "suspended" && (!expiredException(user) || master) && (
            <button
              className="secondary-button"
              disabled={busy}
              onClick={() => void act(() => apiClient.reactivateAccessUser(token, user.id))}
            >
              <RotateCcw size={15} aria-hidden="true" />
              {expiredException(user) ? "Reativar e renovar exceção (90 dias)" : "Reativar acesso"}
            </button>
          )}
        </div>
      )}

      {/* Bloqueio por tentativas erradas (2026-10-22, proposta de IAM §7.2):
          o Keycloak libera sozinho em minutos; o Máster libera na hora.
          O dado só vem pro Máster. */}
      {master && user.login_lockout?.locked && (
        <div className="user-detail-callout is-warning" role="status">
          <Lock size={16} aria-hidden="true" />
          <p>
            Login bloqueado por tentativas erradas ({user.login_lockout.failures} seguidas
            {user.login_lockout.last_failure_at && <>, a última em {formatDateTime(serverDate(user.login_lockout.last_failure_at))}</>}
            ). Libera sozinho em alguns minutos; desbloquear libera agora.
          </p>
          <button
            className="secondary-button"
            disabled={busy}
            onClick={() => void act(() => apiClient.unlockAccessUserLogin(token, user.id), "Login desbloqueado.")}
          >
            <LockOpen size={15} aria-hidden="true" />
            Desbloquear
          </button>
        </div>
      )}

      {user.status === "disabled" && (
        <div className="user-detail-callout is-muted">
          <Power size={16} aria-hidden="true" />
          <p>Conta inativa: a pessoa não entra no ReputAItion e não tem vínculos ativos.</p>
          {master && (
            <button
              className="secondary-button"
              disabled={busy}
              onClick={() => void act(() => apiClient.setAccessUserEnabled(token, user.id, true))}
            >
              Reativar conta
            </button>
          )}
        </div>
      )}

      {/* 2. Cadastro */}
      <section className="user-detail-section" aria-labelledby="user-detail-cadastro">
        <h4 id="user-detail-cadastro">Cadastro</h4>
        <div className="access-rename">
          <label className="field">
            <span>Nome no cadastro</span>
            <input value={nextName} maxLength={120} onChange={event => setNextName(event.target.value)} />
          </label>
          {nameChanged && (
            <div className="user-detail-field-actions">
              <button type="button" className="text-button" onClick={() => setNextName(user.name)}>Desfazer</button>
              <button
                className="primary-button"
                disabled={busy}
                onClick={() => void act(() => apiClient.renameAccessUser(token, user.id, nextName), "Nome alterado.")}
              >
                Alterar nome
              </button>
            </div>
          )}
        </div>

        {/* O Administrador troca o cargo de quem é do núcleo dele, entre
            os cargos abaixo do dele (2026-10-22); `allowedRoles` já vem
            limitado. Ninguém troca o próprio — o servidor recusa. */}
        {(
          <div className="access-role-change">
            <label className="field">
              <span>Cargo</span>
              <Select value={nextRole} onChange={event => setNextRole(event.target.value as AccessUser["role"])}>
                {allowedRoles.map(item => (
                  <option key={item} value={item}>{ROLE_LABEL[item]}</option>
                ))}
              </Select>
            </label>
            {roleChanged && (
              <div className="user-detail-field-actions">
                <button type="button" className="text-button" onClick={() => setNextRole(user.role)}>Desfazer</button>
                <button
                  className="primary-button"
                  disabled={busy || (needsNucleus && adminGroups.length === 0)}
                  title={needsNucleus && adminGroups.length === 0 ? "Escolha pelo menos um núcleo" : undefined}
                  onClick={() =>
                    void act(() =>
                      apiClient.changeAccessUserRole(token, user.id, {
                        role: nextRole,
                        group_ids: needsNucleus ? adminGroups.map(group => group.id) : [],
                        company_ids: []
                      })
                    )
                  }
                >
                  Alterar cargo
                </button>
              </div>
            )}
            {roleChanged && resetsScope && (
              <p className="user-detail-hint is-warning">
                <AlertTriangle size={14} aria-hidden="true" />
                Mudar de {ROLE_LABEL[user.role]} para {ROLE_LABEL[nextRole]} remove os acessos atuais (
                {[
                  user.groups.length > 0 && plural(user.groups.length, "núcleo", "núcleos"),
                  user.companies.length > 0 && plural(user.companies.length, "empresa", "empresas")
                ].filter(Boolean).join(" e ")}
                ). {nextRole === "master"
                  ? "O Máster enxerga todas as empresas."
                  : nextRole === "administrator"
                    ? "Os núcleos dele são os escolhidos abaixo."
                    : "Os vínculos do novo cargo são dados depois, aqui embaixo."}
              </p>
            )}
            {needsNucleus && (
              <AccessLinkList
                title="Núcleos do novo Administrador"
                hint="Pelo menos um. Ele enxerga e administra as empresas de cada núcleo."
                searchPlaceholder="Digite o nome do núcleo"
                detail={item => item.description}
                icon={<Network size={14} />}
                isActive={item => item.is_active}
                statusLabels={["Ativo", "Inativo"]}
                unavailable={inactiveGroupReason}
                available={groups.filter(group => !adminGroups.some(chosen => chosen.id === group.id))}
                linked={adminGroups}
                label={item => item.name}
                onGrant={async id => setAdminGroups(current => [...current, ...groups.filter(group => group.id === id)])}
                onRevoke={async id => setAdminGroups(current => current.filter(group => group.id !== id))}
              />
            )}
          </div>
        )}
      </section>

      {/* 3. Acessos */}
      <section className="user-detail-section" aria-labelledby="user-detail-acessos">
        <h4 id="user-detail-acessos">Acessos</h4>
        {user.status === "disabled" && (
          <p className="access-notice">
            <Info size={15} aria-hidden="true" /> Conta inativa: sem vínculos ativos.
          </p>
        )}
        {user.status === "suspended" && (
          <p className="access-notice">
            <Info size={15} aria-hidden="true" /> Suspenso: os vínculos ficam guardados e voltam com a reativação.
          </p>
        )}
        {user.role === "master" && user.status === "active" && (
          <p className="access-notice">
            <Info size={15} aria-hidden="true" /> O Máster enxerga todas as empresas — não precisa de vínculo.
          </p>
        )}
        {/* Pendente também recebe vínculo (2026-10-22): a conta já nasce
            com acesso. Núcleo é só o Máster quem dá (RN-05); o
            Administrador vê os núcleos da pessoa e cuida das empresas. */}
        {(user.status === "active" || user.status === "pending") && user.role !== "master" && (
          <>
            <AccessLinkList
              title="Núcleos"
              hint={master
                ? "Enxerga todas as empresas de cada núcleo."
                : "Enxerga todas as empresas de cada núcleo. Quem põe e tira pessoas de núcleos é o Máster."}
              readOnly={!master}
              searchPlaceholder="Digite o nome do núcleo"
              detail={item => item.description}
              icon={<Network size={14} />}
              isActive={item => item.is_active}
              statusLabels={["Ativo", "Inativo"]}
              unavailable={inactiveGroupReason}
              available={groups.filter(group => !user.groups.some(current => current.id === group.id))}
              linked={user.groups}
              label={item => item.name}
              onGrant={id => act(() => apiClient.grantGroupUser(token, id as number, user.id)).then(() => undefined)}
              onRevoke={id => act(() => apiClient.revokeGroupUser(token, id as number, user.id)).then(() => undefined)}
            />
            <AccessLinkList
              title="Empresas específicas"
              hint="Além das que vêm pelos núcleos."
              searchPlaceholder="Digite o nome da empresa"
              icon={<Building2 size={14} />}
              isActive={item => item.is_active}
              available={companies.filter(company => !user.companies.some(current => current.id === company.id))}
              linked={user.companies}
              label={item => item.name}
              onGrant={id => act(() => apiClient.grantDirectCompany(token, user.id, id as string)).then(() => undefined)}
              onRevoke={id => act(() => apiClient.revokeDirectCompany(token, user.id, id as string)).then(() => undefined)}
            />
          </>
        )}
      </section>

      {/* 4. Gerenciar conta */}
      {(canManage || (master && loginMode) || user.status !== "disabled") && (
        <section className="user-detail-section" aria-labelledby="user-detail-conta">
          <h4 id="user-detail-conta">Gerenciar conta</h4>
          <div className="access-manage">
            {canManage && (
              <ManageRow
                title="Suspender por afastamento"
                description="Pausa o acesso sem perder nada: cargo e vínculos ficam guardados até a reativação."
              >
                {!suspending && (
                  <button className="secondary-button" disabled={busy} onClick={() => setSuspending(true)}>
                    <PauseCircle size={15} aria-hidden="true" />
                    Suspender
                  </button>
                )}
              </ManageRow>
            )}
            {suspending && (
              <div className="access-suspend-form">
                <label className="field">
                  <span>Observação (opcional)</span>
                  <input
                    autoFocus
                    value={suspendNote}
                    maxLength={500}
                    placeholder="Ex.: licença até 30/11"
                    onChange={event => setSuspendNote(event.target.value)}
                  />
                </label>
                <div className="access-suspend-actions">
                  <button className="text-button" onClick={() => { setSuspending(false); setSuspendNote(""); }}>
                    Cancelar
                  </button>
                  <button
                    className="primary-button"
                    disabled={busy}
                    onClick={() =>
                      void act(() =>
                        apiClient.suspendAccessUser(token, user.id, { reason: "leave", note: suspendNote.trim() || null })
                      )
                    }
                  >
                    Confirmar suspensão
                  </button>
                </div>
              </div>
            )}

            {master && loginMode && (
              <ManageRow
                title="Forma de entrar"
                tag={LOGIN_METHOD_LABEL[user.login_method]}
                description={
                  google
                    ? "Entra com a conta Google do e-mail cadastrado."
                    : <>
                        Exceção para quem não tem conta Google da empresa.
                        {user.login_exception_until && <> Vale até {formatFullDate(serverDate(user.login_exception_until))}.</>}
                      </>
                }
              >
                {!google && user.status !== "suspended" && (
                  <button
                    className="secondary-button"
                    disabled={busy}
                    onClick={() => void act(() => apiClient.setAccessUserLoginMethod(token, user.id, "password"), "Exceção renovada por 90 dias.")}
                  >
                    <RotateCcw size={15} aria-hidden="true" />
                    Renovar por 90 dias
                  </button>
                )}
                <button className="secondary-button" disabled={busy} onClick={() => setConfirming("login-method")}>
                  <KeyRound size={15} aria-hidden="true" />
                  {google ? "Mudar para usuário e senha" : "Mudar para conta Google"}
                </button>
              </ManageRow>
            )}

            {master && (user.status === "active" || user.status === "suspended") && !(loginMode && google) && (
              <ManageRow
                title="Senha"
                description="Manda à pessoa um e-mail para definir uma senha nova. O link vale 24 horas, e quem escolhe a senha é ela."
              >
                <button className="secondary-button" disabled={busy} onClick={() => setConfirming("reset-password")}>
                  <Mail size={15} aria-hidden="true" />
                  Redefinir senha
                </button>
              </ManageRow>
            )}

            {user.status !== "disabled" && (
              <ManageRow
                title="Inativar conta"
                description="Encerra o acesso. A conta vai para a lista de inativos e pode ser reativada depois."
              >
                <button className="secondary-button access-danger-button" disabled={busy} onClick={() => setConfirming("disable")}>
                  <Power size={15} aria-hidden="true" />
                  Inativar
                </button>
              </ManageRow>
            )}
          </div>
        </section>
      )}

      {confirming === "disable" && (
        <ConfirmDialog
          title={`Inativar ${user.name}?`}
          description="A pessoa perde o acesso ao ReputAItion na hora, e os vínculos com empresas e núcleos saem. A conta fica na lista de inativos e um Máster pode reativá-la quando quiser."
          confirmLabel="Inativar conta"
          danger
          loading={busy}
          onConfirm={() => void confirm(() => apiClient.setAccessUserEnabled(token, user.id, false))}
          onCancel={() => setConfirming(null)}
        />
      )}
      {confirming === "reset-password" && (
        <ConfirmDialog
          title={`Redefinir a senha de ${user.name}?`}
          description={`Um e-mail com o link para definir uma senha nova vai agora para ${user.email}. O link vale 24 horas, e quem escolhe a senha é a pessoa.${sharedIdentityNote(user, "A senha nova vale para todos eles.")}`}
          confirmLabel="Enviar e-mail"
          loading={busy}
          onConfirm={() =>
            void confirm(
              () => apiClient.resetAccessUserPassword(token, user.id),
              `E-mail para redefinir a senha enviado para ${user.email}.`,
              { reload: false }
            )
          }
          onCancel={() => setConfirming(null)}
        />
      )}
      {confirming === "login-method" && (
        <ConfirmDialog
          title={otherMethod === "password" ? "Mudar para usuário e senha?" : "Mudar para conta Google?"}
          description={
            otherMethod === "password"
              ? `${user.name} vira uma exceção que entra com usuário e senha e recebe agora um e-mail para definir a senha. A exceção vale 90 dias; depois o acesso é suspenso até um Máster renovar.${sharedIdentityNote(user, "A senha que a pessoa definir vale para todos eles.")}`
              : `${user.name} passa a entrar com a conta Google de ${user.email}.${loginMode ? sharedIdentityNote(user, "Voltar pro Google apaga a senha da conta, que deixa de valer neles também.") : ""}`
          }
          confirmLabel="Mudar forma de entrar"
          loading={busy}
          onConfirm={() =>
            void confirm(
              () => apiClient.setAccessUserLoginMethod(token, user.id, otherMethod),
              otherMethod === "password"
                ? `Agora entra com usuário e senha. E-mail para definir a senha enviado para ${user.email}.`
                : undefined
            )
          }
          onCancel={() => setConfirming(null)}
        />
      )}
    </>
  );
}
